import ISLAND_ABI from 'abis/island.json'
import { WAGMI_REFETCH_POLICY_NEVER } from 'config/RefetchPolicies'
import { Abi, Address } from 'viem'
import { useReadContract } from 'wagmi'

type TickData = {
  lowerTick: number
  upperTick: number
}

export interface UseIslandTickDataReturn {
  isLoading: boolean
  isError: boolean
  ticks: TickData | null
}

export const useIslandTickData = (islandAddress?: Address): UseIslandTickDataReturn => {
  const contract = {
    address: islandAddress as Address,
    abi: ISLAND_ABI as Abi,
    query: {
      ...WAGMI_REFETCH_POLICY_NEVER,
      enabled: !!islandAddress,
    },
  } as const

  // 1. First Call: Read lowerTick
  const {
    data: lowerTickData,
    isLoading: isLowerLoading,
    isError: isLowerError,
  } = useReadContract({ ...contract, functionName: 'lowerTick' })

  // 2. Second Call: Read upperTick
  const {
    data: upperTickData,
    isLoading: isUpperLoading,
    isError: isUpperError,
  } = useReadContract({ ...contract, functionName: 'upperTick' })

  // 3. Combine Status
  // The hook is loading if EITHER call is loading.
  const isLoading = isLowerLoading || isUpperLoading
  // The hook is in error state if EITHER call has an error.
  const isError = isLowerError || isUpperError

  // 4. Combine Data
  // Data is ready if BOTH calls returned a non-null/non-undefined result.
  const lowerTick = lowerTickData as number | undefined
  const upperTick = upperTickData as number | undefined

  const ticks: TickData | null =
    lowerTick !== undefined && upperTick !== undefined
      ? {
          lowerTick,
          upperTick,
        }
      : null

  // 5. Return the combined result, matching the original signature
  return {
    isLoading,
    isError,
    ticks,
  }
}
